import { faBan, faBoxOpen, faLayerGroup, faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Spinner } from '@shared/components/ui';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { cn } from '@shared/utils/utils';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxGroupModel } from '../../models/PackagingRulesets';
import { getGroupPackagingItemIds } from './boxGroups.utils';

const GROUP_PALETTE: Array<{ colorClass: string; iconClass: string }> = [
  { colorClass: 'bg-bg-success', iconClass: 'text-success' },
  { colorClass: 'bg-bg-info', iconClass: 'text-info' },
  { colorClass: 'bg-badge-bg-status-3', iconClass: 'text-badge-status-3' },
  { colorClass: 'bg-bg-warning', iconClass: 'text-warning' },
  { colorClass: 'bg-badge-bg-status-2', iconClass: 'text-badge-status-2' },
];

const getPalette = (index: number) => GROUP_PALETTE[index % GROUP_PALETTE.length];

interface BoxGroupsSelectorProps {
  groups: BoxGroupModel[];
  selectedGroupId: number | null;
  onSelectGroup: (group: BoxGroupModel) => void;
  disabled?: boolean;
  isLoading?: boolean;
  /** Si se define junto al estado vacío, mismo patrón visual que «Reglas especiales» (título + descripción en tarjeta). */
  emptyTitle?: string;
  /** En modo tarjeta puede omitirse si solo hay título + emptyTextSecond. */
  emptyText?: string;
  /** Segundo párrafo bajo emptyText (ej. tras el punto inicial), solo en modo tarjeta con emptyTitle. */
  emptyTextSecond?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchTestId?: string;
  sortSlot?: ReactNode;
  testIdPrefix: string;
  loadingTestId?: string;
  emptyTestId?: string;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;
  loadMoreTestId?: string;
  /** Opción explícita «sin grupo» (p. ej. sin cajas por defecto). */
  noneOption?: {
    label: string;
    description?: string;
    selected: boolean;
    onSelect: () => void;
    testId?: string;
  };
}

export const BoxGroupsSelector = ({
  groups,
  selectedGroupId,
  onSelectGroup,
  disabled = false,
  isLoading = false,
  emptyTitle,
  emptyText = '',
  emptyTextSecond,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchTestId = 'box-groups-search-input',
  sortSlot,
  testIdPrefix,
  loadingTestId = 'box-groups-loading',
  emptyTestId = 'box-groups-empty',
  hasMore = false,
  isFetchingMore = false,
  onLoadMore,
  loadMoreTestId = 'box-groups-load-more',
  noneOption,
}: BoxGroupsSelectorProps) => {
  const { t } = useTranslation('packagingRulesetsModule');

  return (
    <div className="space-y-4">
      {onSearchChange && (
        <div className="w-full flex items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-subtext"
            />
            <Input
              value={searchValue ?? ''}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={searchPlaceholder}
              data-testid={searchTestId}
              className="pl-9"
            />
          </div>
          {sortSlot}
        </div>
      )}

      <div
        className={cn(
          'grid grid-cols-1 md:grid-cols-2 gap-3 transition-opacity duration-200',
          // Sin grupos no hay tarjetas que atenuar; si aplicamos opacity al vacío, la tarjeta empty casi desaparece.
          disabled && groups.length > 0 && 'opacity-40 pointer-events-none',
        )}
      >
        {isLoading ? (
          <div className="md:col-span-2 flex justify-center py-8" data-testid={loadingTestId}>
            <Spinner size="sm" />
          </div>
        ) : groups.length === 0 ? (
          <div className="md:col-span-2" data-testid={emptyTestId}>
            {emptyTitle ? (
              <div className="mt-4 rounded-xl border border-dashed border-card-border bg-card-bg px-6 py-8 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-bg-info">
                  <FontAwesomeIcon icon={faLayerGroup} className="h-5 w-5 text-info" />
                </div>
                <p className="text-base font-medium text-text">{emptyTitle}</p>
                {emptyText.trim() ? (
                  <p className="mx-auto mt-1 max-w-[420px] text-sm text-subtext">{emptyText}</p>
                ) : null}
                {emptyTextSecond ? (
                  <p className={cn('mx-auto max-w-[420px] text-sm text-subtext', emptyText.trim() ? 'mt-2' : 'mt-1')}>
                    {emptyTextSecond}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-subtext text-center">{emptyText}</p>
              </div>
            )}
          </div>
        ) : null}

        {noneOption && (
          <Button
            key={`${testIdPrefix}-none`}
            data-testid={noneOption.testId ?? `${testIdPrefix}-none`}
            onClick={noneOption.onSelect}
            variant="outline"
            className={cn(
              'h-auto w-full justify-start rounded-lg border p-3 text-left transition-colors md:col-span-2',
              noneOption.selected ? 'border-primary bg-card-bg' : 'border-card-border hover:bg-hover-press',
            )}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                <FontAwesomeIcon icon={faBan} className="h-3.5 w-3.5 text-subtext" />
              </div>
              <div>
                <p className="font-medium text-text">{noneOption.label}</p>
                {noneOption.description ? <p className="text-xs text-subtext">{noneOption.description}</p> : null}
              </div>
            </div>
          </Button>
        )}

        {groups.map((group, index) => {
          const palette = getPalette(index);
          const availableCount = getGroupPackagingItemIds(group).length;
          const isSelected = selectedGroupId === group.id;

          return (
            <Button
              key={`${testIdPrefix}-${group.id}`}
              data-testid={`${testIdPrefix}-${group.id}`}
              onClick={() => onSelectGroup(group)}
              variant="outline"
              className={cn(
                'h-auto w-full justify-start rounded-lg border p-3 text-left transition-colors',
                isSelected ? 'border-primary bg-card-bg' : 'border-card-border hover:bg-hover-press',
              )}
            >
              <div className="flex items-start gap-3">
                <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center', palette.colorClass)}>
                  <FontAwesomeIcon icon={faLayerGroup} className={cn('h-3.5 w-3.5', palette.iconClass)} />
                </div>
                <div>
                  <p className="font-medium text-text">{group.name}</p>
                  <p className="text-xs text-subtext">
                    <FontAwesomeIcon icon={faBoxOpen} className="mr-1 h-3 w-3" />
                    {t('boxes.defaultGroup.availableBoxes', { count: availableCount })}
                  </p>
                </div>
              </div>
            </Button>
          );
        })}
      </div>

      {hasMore && onLoadMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={isFetchingMore}
            data-testid={loadMoreTestId}
          >
            {isFetchingMore ? <Spinner size="sm" /> : t('boxes.defaultGroup.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
};
